import React, { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { indexMappings } from "../lib/ioMapping";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Plus, MapPin, Building2, ShieldCheck, Trash2, FileText, BellRing, ExternalLink, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useSiteContext } from "../components/layout/Layout";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../context/AuthContext";
import SiteNfcTag from "../components/acms/SiteNfcTag";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as ReTooltip } from "recharts";
import { tankLevelPct } from "../lib/tankLevel";

const BUILDING_TYPES = ["High-rise", "Industrial", "Warehouse", "Hospital", "Commercial"];

// 4–20 mA endpoints for pressure transmitters. Tanks use the shared calculation (lib/tankLevel.js).
const SENSOR_MIN = 4, SENSOR_MAX = 20;

// Fallback full-scale range when a pressure sensor's meta.range_bar isn't configured yet — must
// be a real full-scale value, never an alarm setpoint (pressure_gauge_scaling_bug.md).
const DEFAULT_MAX_PRESSURE_BAR = 40;
// live-values' "value" is the raw register value (raw 4-20 mA), not bar — same conversion as
// Live Monitor / Hydrant / Sprinkler / Detection System, needed here so Site Health's OK/Critical
// call and the Bar readings shown in the drawer agree with those pages for the same sensor.
function pressureBar(asset, rawValue, ioMap) {
  if (rawValue == null) return null;
  const raw = Number(rawValue);
  const ioMapping = asset.gateway_id && asset.io_point_name ? ioMap?.[`${asset.gateway_id}:${asset.io_point_name}`] : null;
  const maxPressure = ioMapping?.engineering_max != null
    ? Number(ioMapping.engineering_max)
    : (Number(asset.meta?.range_bar) || DEFAULT_MAX_PRESSURE_BAR);
  return Math.min(maxPressure, Math.max(0, ((raw - SENSOR_MIN) / (SENSOR_MAX - SENSOR_MIN)) * maxPressure));
}

function KPIBox({ label, value, tone = "orange" }) {
  const grad = {
    orange: "from-orange-500 to-pink-500",
    green: "from-emerald-400 to-emerald-600",
    red: "from-red-500 to-pink-600",
    blue: "from-sky-400 to-indigo-500",
  };
  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className={`text-xl font-extrabold bg-gradient-to-r ${grad[tone]} bg-clip-text text-transparent`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

export default function SiteManagement() {
  const { can } = usePermissions();
  const { user } = useAuth();
  // Gateways managed from the site drawer follow the same super-admin-only rule.
  const canManageGateways = can("hardware_gateways", "update");
  const { selectedOrg } = useSiteContext();
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [drawerSite, setDrawerSite] = useState(null);
  const [drawerAlerts, setDrawerAlerts] = useState([]);
  const [drawerAssets, setDrawerAssets] = useState([]);
  const [drawerLiveValues, setDrawerLiveValues] = useState({});
  const [ioMap, setIoMap] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [gateways, setGateways] = useState([]);
  const [allGateways, setAllGateways] = useState([]);
  const [gatewayForm, setGatewayForm] = useState({});
  const [editingGateway, setEditingGateway] = useState(null);
  const [linkGatewayId, setLinkGatewayId] = useState("");

  const load = () => api.get("/sites").then((r) => setSites(r.data));
  useEffect(() => { load(); }, []);

  // Load site-specific data when drawer opens
  useEffect(() => {
    if (!drawerSite) return;
    setEditMode(false);
    setEditForm(drawerSite);
    setGatewayForm({});
    setEditingGateway(null);
    setLinkGatewayId("");
    api.get("/alerts", { params: { site_id: drawerSite.site_id, limit: 5 } })
      .then((r) => setDrawerAlerts(r.data)).catch(() => setDrawerAlerts([]));
    api.get("/hardware/gateways", { params: { site_id: drawerSite.site_id } })
      .then((r) => setGateways(r.data || [])).catch(() => setGateways([]));
    // Org-wide list (no site_id filter) so the "Link Existing Gateway" dropdown can offer
    // gateways already registered elsewhere — a single site can have multiple gateways, and
    // a gateway already registered for another site can be relinked here instead of
    // re-creating it (gateway_id is globally unique, so re-creating would just 409).
    api.get("/hardware/gateways")
      .then((r) => setAllGateways(r.data || [])).catch(() => setAllGateways([]));
    api.get("/assets", { params: { site_id: drawerSite.site_id } })
      .then((r) => setDrawerAssets(r.data || [])).catch(() => setDrawerAssets([]));
    api.get("/assets/live-values", { params: { site_id: drawerSite.site_id } })
      .then((r) => setDrawerLiveValues(r.data || {})).catch(() => setDrawerLiveValues({}));
  }, [drawerSite]);

  // IO Mappings give pressure sensors' real calibrated full-scale range (engineering_max) — same
  // source Live Monitor / Hydrant / Sprinkler / Detection System use for the mA -> bar conversion.
  useEffect(() => {
    const gwIds = [...new Set(drawerAssets.filter((a) => a.asset_type === "pressure_sensor").map((a) => a.gateway_id).filter(Boolean))];
    if (!gwIds.length) { setIoMap({}); return; }
    Promise.all(gwIds.map((gwId) => api.get(`/hardware/gateways/${gwId}/io-mappings`).then((r) => r.data || []).catch(() => [])))
      .then((lists) => {
        const map = {};
        Object.assign(map, indexMappings(lists.flat()));
        setIoMap(map);
      });
  }, [drawerAssets]);

  const save = async () => {
    try {
      if (!form.name) { toast.error("Name required"); return; }
      // Super admins create sites on behalf of a client — the site joins the org picked in the header.
      await api.post("/sites", { ...form, org_id: selectedOrg || undefined, building_type: form.building_type || "High-rise" });
      toast.success("Site created"); setOpen(false); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (id) => { if (!window.confirm("Delete site?")) return; await api.delete(`/sites/${id}`); toast.success("Deleted"); setDrawerSite(null); load(); };

  const saveEdit = async () => {
    try {
      if (!editForm.name) { toast.error("Name required"); return; }
      const r = await api.patch(`/sites/${drawerSite.site_id}`, editForm);
      toast.success("Site updated");
      setEditMode(false);
      load();
      if (r.data) setDrawerSite(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const saveGateway = async () => {
    try {
      await api.patch(`/hardware/gateways/${editingGateway.gateway_id}`, gatewayForm);
      toast.success("Gateway updated");
      setGatewayForm({});
      setEditingGateway(null);
      api.get("/hardware/gateways", { params: { site_id: drawerSite.site_id } })
        .then((r) => setGateways(r.data || [])).catch(() => {});
      api.get("/hardware/gateways").then((r) => setAllGateways(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save gateway"); }
  };

  const deleteGateway = async (id) => {
    if (!window.confirm("Delete gateway?")) return;
    try {
      await api.delete(`/hardware/gateways/${id}`);
      toast.success("Gateway deleted");
      api.get("/hardware/gateways", { params: { site_id: drawerSite.site_id } })
        .then((r) => setGateways(r.data || [])).catch(() => {});
      api.get("/hardware/gateways").then((r) => setAllGateways(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const linkExistingGateway = async () => {
    if (!linkGatewayId) return;
    try {
      await api.patch(`/hardware/gateways/${linkGatewayId}`, { site_id: drawerSite.site_id });
      toast.success("Gateway linked to this site");
      setLinkGatewayId("");
      api.get("/hardware/gateways", { params: { site_id: drawerSite.site_id } })
        .then((r) => setGateways(r.data || [])).catch(() => {});
      api.get("/hardware/gateways").then((r) => setAllGateways(r.data || [])).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to link gateway"); }
  };

  const badge = (s) => s === "GR Compliant" ? "bg-emerald-100 text-emerald-700" : s === "Certificate Expired" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";

  // Super admins see every org's sites from /sites; show only the org picked in the header.
  const orgSites = selectedOrg ? sites.filter((s) => s.org_id === selectedOrg) : sites;
  const siteName = (siteId) => sites.find((s) => s.site_id === siteId)?.name || siteId;
  // Gateways not already linked to this site — the dropdown can both attach a brand-new
  // unassigned gateway and re-point one currently registered at a different site.
  const linkableGateways = useMemo(
    () => allGateways.filter((g) => g.site_id !== drawerSite?.site_id),
    [allGateways, drawerSite]
  );

  // Site Health: derived from real linked Asset Registry data (gateway + io_point_name +
  // live-values), not the old /telemetry simulator. A metric with no linked asset is
  // excluded entirely from the OK/Critical count rather than assumed good or bad.
  const siteHealth = useMemo(() => {
    const findLive = (pred) => {
      const asset = drawerAssets.find(pred);
      if (!asset) return null;
      const live = drawerLiveValues[asset.asset_id];
      if (!live || live.value === null || live.value === undefined) return null;
      return { asset, value: Number(live.value), unit: live.unit };
    };
    // findLive.value is the raw register value — fine for the digital pump signal, but a pressure
    // sensor's raw value is 4-20 mA, not bar (goes through pressureBar), and a tank's raw value
    // needs the Level Transmitter formula (tankLevelPct below) before comparing against a % threshold.
    let hydrant = findLive((a) => a.asset_type === "pressure_sensor" && a.system === "hydrant");
    let sprinkler = findLive((a) => a.asset_type === "pressure_sensor" && a.system === "sprinkler");
    if (hydrant) hydrant = { ...hydrant, value: pressureBar(hydrant.asset, hydrant.value, ioMap) };
    if (sprinkler) sprinkler = { ...sprinkler, value: pressureBar(sprinkler.asset, sprinkler.value, ioMap) };
    const ugTank = findLive((a) => a.asset_type === "tank" && (a.meta?.type === "UG" || a.meta?.tank_type === "UG"));
    const ohtTank = findLive((a) => a.asset_type === "tank" && (a.meta?.type === "OHT" || a.meta?.tank_type === "OHT"));
    const mainPump = findLive((a) => a.asset_type === "pump" && a.system === "hydrant");
    return {
      hydrant: hydrant && { ...hydrant, ok: hydrant.value >= 3.5 },
      sprinkler: sprinkler && { ...sprinkler, ok: sprinkler.value >= 5 },
      // pct null = sensor fault / setup incomplete: shown as such, and never counted as OK.
      ugTank: ugTank && { ...ugTank, pct: tankLevelPct(ugTank.asset.meta, ugTank.value), ok: (tankLevelPct(ugTank.asset.meta, ugTank.value) ?? -1) >= 50 },
      ohtTank: ohtTank && { ...ohtTank, pct: tankLevelPct(ohtTank.asset.meta, ohtTank.value), ok: (tankLevelPct(ohtTank.asset.meta, ohtTank.value) ?? -1) >= 50 },
      mainPump: mainPump && { ...mainPump, ok: mainPump.value === 1 },
    };
  }, [drawerAssets, drawerLiveValues, ioMap]);

  const donutData = useMemo(() => {
    const metrics = [siteHealth.hydrant, siteHealth.sprinkler, siteHealth.ugTank, siteHealth.ohtTank, siteHealth.mainPump].filter(Boolean);
    if (!metrics.length) return [];
    const ok = metrics.filter((m) => m.ok).length;
    const bad = metrics.length - ok;
    return [
      { name: "OK", value: ok, color: "#22c55e" },
      { name: "Critical", value: bad, color: "#ef4444" },
    ].filter((d) => d.value > 0);
  }, [siteHealth]);

  return (
    <div className="space-y-6" data-testid="sites-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Site Management</h1>
          <p className="text-sm text-gray-500 mt-1">Click any site card to open the drilldown drawer</p>
        </div>
        {can("site_management", "create") && (
          <Button onClick={() => setOpen(true)} className="f360-gradient border-0" data-testid="add-site-btn"><Plus className="w-4 h-4 mr-1" strokeWidth={2.5} />Add Site</Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {orgSites.map((s) => (
          <div
            key={s.site_id}
            className="glass p-5 rounded-lg text-left hover:border-orange-500/30 transition group relative"
            data-testid={`site-card-${s.site_id}`}
          >
            <button
              onClick={() => setDrawerSite(s)}
              className="absolute inset-0 rounded-lg cursor-pointer"
              data-testid={`site-card-view-${s.site_id}`}
            />
            <div className="flex items-start justify-between relative z-10">
              <div className="w-10 h-10 rounded-lg f360-gradient flex items-center justify-center"><Building2 className="w-5 h-5 text-gray-900" strokeWidth={2.5} /></div>
              <div className="flex items-center gap-2">
                <Badge className={`${badge(s.compliance_status)} border-0`}>{s.compliance_status}</Badge>
                {can("site_management", "update") && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setDrawerSite(s);
                      setTimeout(() => setEditMode(true), 100);
                    }}
                    className="p-1.5 rounded-lg bg-gray-100 hover:bg-orange-100 text-gray-600 hover:text-orange-600 transition opacity-0 group-hover:opacity-100"
                    data-testid={`site-card-edit-${s.site_id}`}
                  >
                    <Pencil className="w-4 h-4" strokeWidth={2.5} />
                  </button>
                )}
              </div>
            </div>
            <h3 className="mt-3 text-lg font-bold group-hover:text-orange-600 transition relative z-10">{s.name}</h3>
            <div className="text-xs text-gray-500 flex items-center gap-1 mt-1 relative z-10"><MapPin className="w-3 h-3" strokeWidth={2.5} />{s.city}, {s.state}</div>
            <div className="grid grid-cols-2 gap-3 mt-4 text-xs relative z-10">
              <div><div className="text-gray-500">Type</div><div className="text-gray-900">{s.building_type}</div></div>
              <div><div className="text-gray-500">Floors</div><div className="text-gray-900">{s.total_floors}</div></div>
              <div><div className="text-gray-500">Hydrants</div><div className="text-gray-900">{s.total_hydrants}</div></div>
              <div><div className="text-gray-500">Sprinkler Zones</div><div className="text-gray-900">{s.total_sprinkler_zones}</div></div>
            </div>
            <div className="mt-4 flex items-center gap-2 text-xs relative z-10">
              <ShieldCheck className="w-3.5 h-3.5 text-orange-400" strokeWidth={2.5} />
              <span className="text-gray-500">NOC:</span><span className="font-mono text-gray-700">{s.fire_noc_number || "—"}</span>
            </div>
          </div>
        ))}
        {!orgSites.length && <div className="glass rounded-lg p-10 text-center text-gray-500 col-span-full">No sites. Click <b>Add Site</b> to create one.</div>}
      </div>

      {/* Add Site dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add Site</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Site Name *</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="site-form-name" /></div>
            <div>
              <Label>Building Type</Label>
              <Select value={form.building_type || "High-rise"} onValueChange={(v) => setForm({ ...form, building_type: v })}>
                <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-gray-200 text-gray-900">
                  {BUILDING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>City</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div><Label>Pin Code</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, pin: e.target.value })} /></div>
            <div><Label>State</Label><Input className="bg-gray-50 border-gray-200 mt-1" defaultValue="Maharashtra" onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
            <div><Label>Fire NOC Number</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, fire_noc_number: e.target.value })} /></div>
            <div><Label>NOC Expiry</Label><Input type="date" className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, fire_noc_expiry: e.target.value })} /></div>
            <div><Label>ACMS Certificate</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, acms_certificate_number: e.target.value })} /></div>
            <div><Label>ACMS Expiry</Label><Input type="date" className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, acms_certificate_expiry: e.target.value })} /></div>
            <div><Label>Assigned Admin</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, assigned_admin: e.target.value })} /></div>
            <div><Label>Total Floors</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, total_floors: Number(e.target.value) })} /></div>
            <div><Label>Building Height (m)</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, building_height: Number(e.target.value) })} /></div>
            <div><Label>Total Hydrants</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, total_hydrants: Number(e.target.value) })} /></div>
            <div><Label>Sprinkler Zones</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, total_sprinkler_zones: Number(e.target.value) })} /></div>
            <div><Label>Contact Person</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></div>
            <div><Label>Emergency Contact</Label><Input className="bg-gray-50 border-gray-200 mt-1" onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="f360-gradient border-0" data-testid="site-form-save">Create Site</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Site detail modal */}
      <Dialog open={!!drawerSite} onOpenChange={(o) => !o && setDrawerSite(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="site-modal">
          {drawerSite && (
            <>
              <DialogHeader className="border-b border-gray-200 pb-4">
                <div className="flex items-center justify-between mb-2 pr-8">
                  <div className="flex items-center gap-2">
                    <Badge className={`${badge(drawerSite.compliance_status)} border-0`}>{drawerSite.compliance_status}</Badge>
                    <Badge variant="outline" className="border-gray-300 text-xs">{drawerSite.building_type}</Badge>
                  </div>
                  {!editMode && can("site_management", "update") && (
                    <Button size="sm" variant="ghost" onClick={() => setEditMode(true)} className="h-8 w-8 p-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100">
                      <Pencil className="w-4 h-4" strokeWidth={2.5} />
                    </Button>
                  )}
                </div>
                <DialogTitle className="text-gray-900 text-2xl">{editMode ? "Edit Site" : drawerSite.name}</DialogTitle>
                {!editMode && <div className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={2.5} />{drawerSite.address}, {drawerSite.city}, {drawerSite.state} {drawerSite.pin}</div>}
              </DialogHeader>

              <div className="grid grid-cols-2 gap-6 py-4">
              <div className="space-y-5">
                {editMode ? (
                  <div className="space-y-4">
                    <div><Label>Site Name *</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} /></div>
                    <div><Label>Building Type</Label>
                      <Select value={editForm.building_type || "High-rise"} onValueChange={(v) => setEditForm({ ...editForm, building_type: v })}>
                        <SelectTrigger className="bg-gray-50 border-gray-200 mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          {BUILDING_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>Address</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} /></div>
                    <div><Label>City</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} /></div>
                    <div><Label>State</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.state || ""} onChange={(e) => setEditForm({ ...editForm, state: e.target.value })} /></div>
                    <div><Label>Pin Code</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.pin || ""} onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })} /></div>
                    <div><Label>Fire NOC Number</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.fire_noc_number || ""} onChange={(e) => setEditForm({ ...editForm, fire_noc_number: e.target.value })} /></div>
                    <div><Label>NOC Expiry</Label><Input type="date" className="bg-gray-50 border-gray-200 mt-1" value={editForm.fire_noc_expiry || ""} onChange={(e) => setEditForm({ ...editForm, fire_noc_expiry: e.target.value })} /></div>
                    <div><Label>ACMS Certificate</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.acms_certificate_number || ""} onChange={(e) => setEditForm({ ...editForm, acms_certificate_number: e.target.value })} /></div>
                    <div><Label>ACMS Expiry</Label><Input type="date" className="bg-gray-50 border-gray-200 mt-1" value={editForm.acms_certificate_expiry || ""} onChange={(e) => setEditForm({ ...editForm, acms_certificate_expiry: e.target.value })} /></div>
                    <div><Label>Contact Person</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.contact_person || ""} onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })} /></div>
                    <div><Label>Emergency Contact</Label><Input className="bg-gray-50 border-gray-200 mt-1" value={editForm.emergency_contact || ""} onChange={(e) => setEditForm({ ...editForm, emergency_contact: e.target.value })} /></div>
                    <div><Label>Total Floors</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" value={editForm.total_floors || ""} onChange={(e) => setEditForm({ ...editForm, total_floors: Number(e.target.value) })} /></div>
                    <div><Label>Building Height (m)</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" value={editForm.building_height || ""} onChange={(e) => setEditForm({ ...editForm, building_height: Number(e.target.value) })} /></div>
                    <div><Label>Total Hydrants</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" value={editForm.total_hydrants || ""} onChange={(e) => setEditForm({ ...editForm, total_hydrants: Number(e.target.value) })} /></div>
                    <div><Label>Sprinkler Zones</Label><Input type="number" className="bg-gray-50 border-gray-200 mt-1" value={editForm.total_sprinkler_zones || ""} onChange={(e) => setEditForm({ ...editForm, total_sprinkler_zones: Number(e.target.value) })} /></div>
                    <div className="flex gap-2 pt-4">
                      <Button onClick={saveEdit} className="flex-1 f360-gradient border-0" data-testid="site-edit-save">Save Changes</Button>
                      <Button onClick={() => { setEditMode(false); setEditForm(drawerSite); }} variant="ghost" className="flex-1" data-testid="site-edit-cancel">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                {/* Section 1 — Site info */}
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Site Information</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Fire NOC</div><div className="font-mono mt-0.5">{drawerSite.fire_noc_number || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">ACMS Cert</div><div className="font-mono mt-0.5">{drawerSite.acms_certificate_number || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Contact</div><div className="font-bold mt-0.5">{drawerSite.contact_person || "—"}</div></div>
                    <div className="bg-gray-50 rounded-lg p-3"><div className="text-gray-500">Emergency</div><div className="font-mono mt-0.5">{drawerSite.emergency_contact || "—"}</div></div>
                  </div>
                </section>

                {/* Section 2 — KPIs */}
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">Infrastructure</div>
                  <div className="grid grid-cols-2 gap-2">
                    <KPIBox label="Floors" value={drawerSite.total_floors || 0} tone="blue" />
                    <KPIBox label="Hydrants" value={drawerSite.total_hydrants || 0} tone="orange" />
                    <KPIBox label="Sprinkler Zones" value={drawerSite.total_sprinkler_zones || 0} tone="orange" />
                    <KPIBox label="Height (m)" value={drawerSite.building_height || 0} tone="blue" />
                  </div>
                </section>

                {/* Section 3 — Health donut */}
                <section>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2 font-bold">System Health</div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    {donutData.length ? (
                      <ResponsiveContainer width="100%" height={180}>
                        <PieChart>
                          <Pie data={donutData} cx="50%" cy="50%" innerRadius={50} outerRadius={75} dataKey="value" stroke="none">
                            {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                          </Pie>
                          <ReTooltip contentStyle={{ background: "#0a0f1e", border: "1px solid #333" }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <div className="text-gray-500 text-xs py-6 text-center">No linked sensors yet — link assets in the Asset Registry to see real data here.</div>}
                    <div className="grid grid-cols-2 gap-2 text-[11px] mt-2">
                      <div>Hydrant: {siteHealth.hydrant ? <span className={siteHealth.hydrant.ok ? "text-emerald-700" : "text-red-700"}>{siteHealth.hydrant.value.toFixed(2)} {siteHealth.hydrant.unit || "Bar"}</span> : <span className="text-gray-400">not linked</span>}</div>
                      <div>Sprinkler: {siteHealth.sprinkler ? <span className={siteHealth.sprinkler.ok ? "text-emerald-700" : "text-red-700"}>{siteHealth.sprinkler.value.toFixed(2)} {siteHealth.sprinkler.unit || "Bar"}</span> : <span className="text-gray-400">not linked</span>}</div>
                      <div>UG Tank: {siteHealth.ugTank ? <span className={siteHealth.ugTank.ok ? "text-emerald-700" : "text-red-700"}>{siteHealth.ugTank.pct == null ? "sensor fault / setup" : `${siteHealth.ugTank.pct.toFixed(0)}%`}</span> : <span className="text-gray-400">not linked</span>}</div>
                      <div>OHT: {siteHealth.ohtTank ? <span className={siteHealth.ohtTank.ok ? "text-emerald-700" : "text-red-700"}>{siteHealth.ohtTank.pct == null ? "sensor fault / setup" : `${siteHealth.ohtTank.pct.toFixed(0)}%`}</span> : <span className="text-gray-400">not linked</span>}</div>
                    </div>
                  </div>
                </section>

                {/* Section 5 — Actions */}
                <section className="space-y-2">
                  {/* Hidden for now (2026-11-03) — opened the old /acms page, not Live Monitor. */}
                  {/* <Link to="/acms" onClick={() => setDrawerSite(null)}>
                    <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid="site-live-btn"><ExternalLink className="w-4 h-4 mr-2" strokeWidth={2.5} /> View Live Monitor</Button>
                  </Link> */}
                  <Link to="/reports" onClick={() => setDrawerSite(null)}>
                    <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900" data-testid="site-report-btn"><FileText className="w-4 h-4 mr-2" strokeWidth={2.5} /> Download GR Report</Button>
                  </Link>
                  {can("site_management", "delete") && (
                    <Button onClick={() => del(drawerSite.site_id)} className="w-full bg-red-50 hover:bg-red-100 border border-red-200 text-red-700" data-testid="site-delete-btn"><Trash2 className="w-4 h-4 mr-2" strokeWidth={2.5} /> Delete Site</Button>
                  )}
                </section>
                  </>
                )}
              </div>

              {/* Right column — Gateway Registration */}
              <div className="space-y-4 border-l border-gray-200 pl-6">
                <div>
                  <h3 className="text-sm uppercase tracking-widest text-gray-500 mb-3 font-bold">Gateway Registration</h3>
                  <div className="space-y-3">
                    {gateways.map((gw) => (
                      <div key={gw.gateway_id} className="bg-gray-50 rounded-lg p-3 flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-mono text-sm font-bold text-gray-900">{gw.gateway_id}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{gw.gateway_name || "—"}</div>
                          <div className="text-[10px] text-gray-400 mt-1">{gw.hardware_model || "Generic"}</div>
                        </div>
                        {canManageGateways && <div className="flex gap-1">
                          <button
                            onClick={() => { setEditingGateway(gw); setGatewayForm(gw); }}
                            className="p-1.5 rounded hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                            data-testid={`gateway-edit-${gw.gateway_id}`}
                          >
                            <Pencil className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </button>
                          <button
                            onClick={() => deleteGateway(gw.gateway_id)}
                            className="p-1.5 rounded hover:bg-red-100 text-red-600 hover:text-red-700"
                            data-testid={`gateway-delete-${gw.gateway_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                          </button>
                        </div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Link an existing gateway (registered elsewhere via the Hardware Gateways
                    page, or unassigned) to this site, instead of re-creating it — a single
                    site can have multiple gateways, so this adds to the list above rather
                    than replacing it. */}
                {canManageGateways && <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-700 mb-3">Link Existing Gateway</h4>
                  {linkableGateways.length === 0 ? (
                    <div className="text-[11px] text-gray-500">No other registered gateways available to link.</div>
                  ) : (
                    <div className="flex gap-2">
                      <Select value={linkGatewayId} onValueChange={setLinkGatewayId}>
                        <SelectTrigger className="bg-white border-gray-200 h-8 text-xs flex-1" data-testid="link-gateway-select"><SelectValue placeholder="Select a gateway…" /></SelectTrigger>
                        <SelectContent className="bg-white border-gray-200 text-gray-900">
                          {linkableGateways.map((g) => (
                            <SelectItem key={g.gateway_id} value={g.gateway_id}>
                              {g.gateway_name || g.gateway_id} ({g.gateway_id}) — {g.site_id ? `currently at ${siteName(g.site_id)}` : "unassigned"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button onClick={linkExistingGateway} disabled={!linkGatewayId} size="sm" className="h-8 text-xs bg-blue-600 hover:bg-blue-700 text-white" data-testid="link-gateway-btn">
                        Link
                      </Button>
                    </div>
                  )}
                </div>}

                {/* NFC site tag — one per site; tapping it opens this site's Live Monitor */}
                <SiteNfcTag siteId={drawerSite.site_id} siteName={drawerSite.name} canManage={user?.role === "super_admin"} />

                {/* Edit Gateway Form — only rendered once the pencil icon above is clicked.
                    Creating a brand-new gateway no longer happens from this drawer; use the
                    Hardware Gateways page or the "Link Existing Gateway" dropdown above. */}
                {editingGateway && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-blue-900 mb-3">Edit Gateway</h4>
                    <div className="space-y-2 text-xs">
                      <div>
                        <Label className="text-[10px]">Gateway ID / IMEI</Label>
                        <Input
                          className="bg-white border-blue-200 mt-0.5 h-8 text-xs"
                          value={gatewayForm.gateway_id || ""}
                          disabled
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Gateway Name</Label>
                        <Input
                          className="bg-white border-blue-200 mt-0.5 h-8 text-xs"
                          placeholder="e.g., Main Hydrant Gateway"
                          value={gatewayForm.gateway_name || ""}
                          onChange={(e) => setGatewayForm({ ...gatewayForm, gateway_name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px]">Hardware Model</Label>
                        <Select value={gatewayForm.hardware_model || "WT-460M"} onValueChange={(v) => setGatewayForm({ ...gatewayForm, hardware_model: v })}>
                          <SelectTrigger className="bg-white border-blue-200 mt-0.5 h-8 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-white border-gray-200 text-gray-900">
                            <SelectItem value="WT-460M">WT-460M</SelectItem>
                            <SelectItem value="Generic MQTT">Generic MQTT</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button
                          onClick={saveGateway}
                          size="sm"
                          className="flex-1 h-7 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="gateway-save"
                        >
                          Update
                        </Button>
                        <Button
                          onClick={() => { setEditingGateway(null); setGatewayForm({}); }}
                          size="sm"
                          variant="ghost"
                          className="flex-1 h-7 text-xs"
                          data-testid="gateway-cancel"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
